import { useState, useCallback, useMemo, useEffect } from 'react';
import { BookingConfig, BookingFormData, UseBookingFormReturn } from '../types/booking';
import { toast } from '@/hooks/use-toast';

const initialFormData: BookingFormData = {
  name: '',
  email: '',
  phone: '',
  date: '',
  time: '',
  message: '',
  serviceType: '',
  partySize: 1,
  specialRequests: '',
  preferredStaff: '',
  meetingType: 'in-person',
  videoCallPlatform: '',
  selectedPricing: '',
};

export const useBookingForm = (config: BookingConfig, onFieldChange?: (field: string, value: any) => void): UseBookingFormReturn => {
  const [formData, setFormData] = useState<BookingFormData>(initialFormData);
  const [customFieldData, setCustomFieldData] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Generate available time slots based on selected date
  const availableTimeSlots = useMemo(() => {
    if (!formData.date || !config.timeSlots.enabled) return [];

    const selectedDay = new Date(formData.date).toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
    const dayAvailability = config.timeSlots.availability[selectedDay];

    if (!dayAvailability) return [];

    return (dayAvailability.slots || []).map(time => ({
      time,
      available: true,
      label: time,
    }));
  }, [formData.date, config.timeSlots]);

  // Get visible custom fields based on current step and conditions
  const visibleCustomFields = useMemo(() => {
    return config.customFields.filter(field => {
      // Add any conditional logic here if needed
      return true;
    });
  }, [config.customFields]);

  // Clear errors when language changes to regenerate them with new translations
  useEffect(() => {
    setErrors({});
  }, [config.localization.language]);

  const updateField = useCallback((field: keyof BookingFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const updateCustomField = useCallback((field: string, value: any) => {
    setCustomFieldData(prev => ({ ...prev, [field]: value }));

    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const validateCurrentStep = useCallback(() => {
    const newErrors: { [key: string]: string } = {};
    const enabledSections = config.sections.filter(s => s.enabled).sort((a, b) => a.order - b.order);
    const currentSectionData = enabledSections[currentStep];

    console.log('validateCurrentStep - currentSectionData:', currentSectionData);

    if (!currentSectionData) return true;

    // Only validate fields for the current step
    const currentFields = currentSectionData.fields;
    console.log('validateCurrentStep - currentFields:', currentFields);
    console.log('validateCurrentStep - formData:', formData);

    currentFields.forEach(fieldId => {
      console.log('Validating field:', fieldId);
      switch (fieldId) {
        case 'name':
          if (config.fields.name && !formData.name.trim()) {
            newErrors.name = config.localization.translations['validation.nameRequired'] || 'Name is required';
            console.log('Name validation failed');
          }
          break;
        case 'email':
          if (config.fields.email && !formData.email.trim()) {
            newErrors.email = config.localization.translations['validation.emailRequired'] || 'Email is required';
            console.log('Email validation failed');
          } else if (config.fields.email && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = config.localization.translations['validation.emailInvalid'] || 'Email is invalid';
            console.log('Email format validation failed');
          }
          break;
        case 'phone':
          if (config.fields.phone && !formData.phone.trim()) {
            newErrors.phone = config.localization.translations['validation.phoneRequired'] || 'Phone is required';
            console.log('Phone validation failed');
          }
          break;
        case 'date':
          if (config.features.calendar && !formData.date) {
            newErrors.date = config.localization.translations['validation.dateRequired'] || 'Date is required';
            console.log('Date validation failed');
          }
          break;
        case 'time':
          if (config.features.timeSlots && !formData.time) {
            newErrors.time = config.localization.translations['validation.timeRequired'] || 'Time is required';
            console.log('Time validation failed');
          }
          break;
        case 'meetingType':
          if (config.fields.meetingType && !formData.meetingType) {
            newErrors.meetingType = config.localization.translations['validation.meetingTypeRequired'] || 'Meeting type is required';
            console.log('Meeting type validation failed');
          }
          break;
      }
    });

    console.log('validateCurrentStep - errors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, currentStep, config]);

  const validateForm = useCallback(() => {
    const newErrors: { [key: string]: string } = {};

    // Basic field validation
    if (config.fields.name && !formData.name.trim()) {
      newErrors.name = config.localization.translations['validation.nameRequired'] || 'Name is required';
    }

    if (config.fields.email && !formData.email.trim()) {
      newErrors.email = config.localization.translations['validation.emailRequired'] || 'Email is required';
    } else if (config.fields.email && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = config.localization.translations['validation.emailInvalid'] || 'Email is invalid';
    }

    if (config.fields.phone && !formData.phone.trim()) {
      newErrors.phone = config.localization.translations['validation.phoneRequired'] || 'Phone is required';
    }

    if (config.features.calendar && !formData.date) {
      newErrors.date = config.localization.translations['validation.dateRequired'] || 'Date is required';
    }

    if (config.features.timeSlots && !formData.time) {
      newErrors.time = config.localization.translations['validation.timeRequired'] || 'Time is required';
    }

    if (config.fields.meetingType && !formData.meetingType) {
      newErrors.meetingType = config.localization.translations['validation.meetingTypeRequired'] || 'Meeting type is required';
    }

    // Validate custom fields
    config.customFields.forEach(field => {
      if (field.required && !customFieldData[field.id]) {
        newErrors[field.id] = config.localization.translations[`validation.${field.id}Required`] || `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, customFieldData, config]);



  const submitForm = useCallback(async (): Promise<boolean> => {
    if (!validateForm()) {
      return false;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        ...customFieldData,
        timestamp: new Date().toISOString(),
      };

      // Mock submission for testing when endpoint is '#'
      if (config.submission.endpoint === '#') {
        // Show booking progress
        toast({
          title: config.localization.translations['toast.saving'] || "Saving appointment...",
          description: config.localization.translations['toast.pleaseWait'] || "Please wait a moment.",
        });

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Show booking success
        toast({
          title: config.localization.translations['toast.saved'] || "✅ Appointment saved!",
          description: config.localization.translations['toast.successMessage'] || "Your appointment has been successfully saved!",
        });

        setIsSubmitted(true);
        return true;
      }

      // Show booking progress
      toast({
        title: config.localization.translations['toast.saving'] || "Saving appointment...",
        description: config.localization.translations['toast.pleaseWait'] || "Please wait a moment.",
      });

      // Real API call for booking submission
      const response = await fetch(config.submission.endpoint, {
        method: config.submission.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        throw new Error('Booking submission failed');
      }

      console.log('✅ Booking submitted successfully');

      // Show booking success
      toast({
        title: config.localization.translations['toast.saved'] || "✅ Appointment saved!",
        description: config.localization.translations['toast.successMessage'] || "Your appointment has been successfully saved!",
      });

      setIsSubmitted(true);
      return true;
    } catch (error) {
      console.error('❌ Form submission error:', error);
      toast({
        title: config.localization.translations['toast.error'] || "❌ Error saving",
        description: config.localization.translations['toast.errorMessage'] || "Something went wrong while saving your appointment. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, customFieldData, config, validateForm]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setCustomFieldData({});
    setErrors({});
    setIsSubmitting(false);
    setIsSubmitted(false);
    setCurrentStep(0);
  }, []);

  const nextStep = useCallback((): boolean => {
    console.log('nextStep called, currentStep:', currentStep);
    const enabledSections = config.sections.filter(s => s.enabled).sort((a, b) => a.order - b.order);
    const currentSectionData = enabledSections[currentStep];
    console.log('currentSectionData:', currentSectionData);

    // Skip validation for hero section
    if (currentSectionData?.id !== 'hero') {
      console.log('Validating current step...');
      // Validate current step before proceeding
      if (!validateCurrentStep()) {
        console.log('Validation failed');
        return false;
      }
      console.log('Validation passed');
    }

    if (currentStep < enabledSections.length - 1) {
      console.log('Advancing to next step');
      setCurrentStep(prev => prev + 1);
      return true;
    }
    console.log('Already at last step');
    return false;
  }, [currentStep, config.sections, validateCurrentStep]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const getFieldError = useCallback((field: string) => {
    return errors[field];
  }, [errors]);

  const handleFieldChange = (field: string, value: any) => {
    if (['name', 'email', 'phone', 'date', 'time', 'message', 'serviceType', 'partySize', 'specialRequests', 'preferredStaff', 'meetingType'].includes(field)) {
      updateField(field as any, value);
    } else {
      updateCustomField(field, value);
    }

    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  return {
    formData,
    customFieldData,
    errors,
    isSubmitting,
    isSubmitted,
    currentStep,
    availableTimeSlots,
    visibleCustomFields,
    updateField,
    updateCustomField,
    submitForm,
    resetForm,
    nextStep,
    previousStep,
    setCurrentStep,
    getFieldError,
  };
}; 